/* ─────────────────────────────────────────────────────────────
 *  Awin consent bridge (RGPD / DSA).
 *
 *  Awin is our affiliate-link monetization layer (replaces Skimlinks).
 *  Its MasterTag drops third-party cookies to attribute outbound
 *  clicks/purchases, so it falls under the "marketing" cookie category
 *  in our GDPR banner — exactly like Google AdSense (`AdSlot.jsx`).
 *
 *  This module bridges the existing ConsentBanner (localStorage key
 *  `tofbox_gdpr_consent`, event `tofbox_consent_changed`) with the
 *  Awin MasterTag loader:
 *
 *    • marketing === true  → inject the script tag in <head>.
 *    • marketing === false → remove the script tag; third-party
 *      cookies on Awin's own domain expire naturally once the script
 *      is gone and can't be cleared from JS.
 *
 *  Awin's MasterTag observes the page for outbound merchant links, so
 *  links added AFTER load (React Router navigations, lazy-rendered
 *  lists, etc.) are picked up automatically once the script is loaded.
 * ──────────────────────────────────────────────────────────── */

import { Capacitor } from '@capacitor/core';
import { getConsent } from '../components/ads/ConsentBanner';

// Publisher-specific MasterTag URL — Awin keys the reporting off the
// numeric Publisher ID (3109374) in the path.
const AWIN_SRC = 'https://www.dwin2.com/pub.3109374.min.js';
const SCRIPT_ID = 'tofbox-awin-mastertag';

function hasMarketingConsent() {
  const c = getConsent();
  return !!(c && c.marketing === true);
}

/** Idempotently inject the Awin MasterTag in <head>. */
function injectScript() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SCRIPT_ID)) return; // already present
  const s = document.createElement('script');
  s.id = SCRIPT_ID;
  s.type = 'text/javascript';
  s.async = true;
  s.src = AWIN_SRC;
  document.head.appendChild(s);
}

/** Remove the loader tag. */
function removeScript() {
  if (typeof document === 'undefined') return;
  const s = document.getElementById(SCRIPT_ID);
  if (s) s.remove();
}

function sync() {
  if (hasMarketingConsent()) injectScript();
  else removeScript();
}

/** Call once at app startup; re-syncs whenever consent changes. */
export function initAwinConsent() {
  // Not in the store apps: following purchases on merchants' sites counts as
  // tracking for Apple (it would need the App Tracking Transparency prompt),
  // and the apps' web views block its cookies anyway.
  if (Capacitor.isNativePlatform()) return;
  sync();
  if (typeof window !== 'undefined') {
    window.addEventListener('tofbox_consent_changed', sync);
  }
}
